// internationalization
import type { TFunction } from 'i18next';

// shared components
import type { DanishAddressFieldValue } from '@/components/ui/form';

// relative
import { unitTypeInputValueSchema } from './unitTypeInput.schema';
import type {
  RecordedDataType,
  SelectedUnitType,
  UnitInputMode,
  UnitTypeInputDefinition,
  UnitTypeInputFormValue,
} from './unitTypeInput.schema';

// libraries
import { z } from 'zod';

// Re-exported for backwards compatibility - these types now live in the
// shared `unitTypeInput.schema.ts` (used by both the create and edit unit
// forms) rather than being declared locally here.
export type {
  RecordedDataType,
  SelectedUnitType,
  UnitInputMode,
  UnitTypeInputDefinition,
  UnitTypeInputFormValue,
};

export type UnitTag = {
  Text: string;
};

export function createUnitFormSchema(t: TFunction) {
  return z
    .object({
      name: z.string().min(1, t('input.required', { ns: 'validation' })),
      description: z.string(),
      address: z
        .custom<DanishAddressFieldValue>()
        .refine(
          (value) =>
            Boolean(value?.displayName?.trim()) &&
            Number.isFinite(value?.latitude) &&
            Number.isFinite(value?.longitude),
          { message: t('input.required', { ns: 'validation' }) },
        ),
      tags: z.array(z.string()),
      unitType: z
        .custom<SelectedUnitType | null>()
        .refine((value) => value != null, {
          message: t('input.required', { ns: 'validation' }),
        }),
      unitTypeInputs: z.record(z.string(), unitTypeInputValueSchema),
    })
    .superRefine((values, ctx) => {
      const invalidInputNames = Object.entries(values.unitTypeInputs)
        .filter(
          ([, inputData]) =>
            inputData.inputType === 'sensorId' &&
            inputData.sensorId &&
            !inputData.channelNumber,
        )
        .map(([inputId]) => {
          const input = values.unitType?.UnitTypeInputs?.find(
            (i) => i.UnitTypeInputId === parseInt(inputId, 10),
          );
          return input?.Name ?? 'Unknown';
        });

      if (invalidInputNames.length > 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['unitTypeInputs'],
          message: t('units.pleaseSelectChannelNumbers', {
            ns: 'units-gateways-sensors',
            names: invalidInputNames.join(', '),
          }),
        });
      }
    });
}

export type CreateUnitFormValues = z.infer<
  ReturnType<typeof createUnitFormSchema>
>;
